import React, { useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';

import { Theme } from '@app/theme';
import { CenteredLoader } from '@app/core';
import { Session, StudyHistoryEvent, useGetSessionHistory } from '@app/api';
import {
  EventHistory,
  HistoryEventDisplayConfigItem,
  SessionBoxInfo,
} from 'components';
import { SessionChangedHistoryText } from './SessionChangedHistoryText';
import { RecommendationHistory } from './RecommendationHistory';
import classNames from 'classnames';
import { OptionsList } from './Admin/OptionsList';
import { TargetCard } from './Admin/TargetCard';
import { AdminNoteCard } from './Admin/AdminNoteCard';
import { RecommendationCard } from './Admin/RecommendationCard';
import { ReferralCard } from './Admin/ReferralCard';
import { Trans, useTranslation } from 'react-i18next';
import { BillingCard } from './Admin/BillingCard';

const useStyles = createUseStyles((theme: Theme) => ({
  wrapper: {
    height: '100%',
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 500px',
    gap: theme.marginBase * 2,
    gridAutoRows: 'minmax(20px, auto)',
    justifyContent: 'center',

    '@media (max-width: 1600px)': {
      gridTemplateColumns: '1fr 500px',
    },
    '@media (max-width: 1350px)': {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    borderWidth: 0,
    gridRowEnd: 'span 2',
  },
  cardSmall: {
    gridRowEnd: 'span 1',
  },
  cardHigh: {
    gridRowEnd: 'span 4',
  },
  cardHistory: {
    gridColumn: 3,
    gridRow: '1 / 20',
    '@media (max-width: 1600px)': {
      gridColumn: 2,
      gridRow: '1 / 20',
    },
    '@media (max-width: 1350px)': {
      gridColumn: 1,
      gridRow: 1,
    },
  },
  cardTitle: {
    ...theme.fonts.h4,
    color: theme.colors.inkSecondary,
    marginBottom: theme.marginBase * 3,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  containerAdditional: {
    background: theme.colors.white,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase,
  },
  insideBoxText: {
    ...theme.fonts.label,
    fontWeight: 400,
  },
  billingCard: {
    borderWidth: 0,
    gridRowEnd: 'span 2',
    gridColumn: '1 / span 2',
  },
  largeCard: {
    borderWidth: 0,
    gridRowEnd: 'span 2',
    gridColumn: '1 / span 2',
    '@media (max-width: 1600px)': {
      gridColumn: 1,
    },
  },
}));

interface Props {
  session: Session;
}

const eventConfig: Record<
  string,
  HistoryEventDisplayConfigItem<StudyHistoryEvent> | string
> = {
  NoteAdded: {
    icon: 'ri-sticky-note-line',
    getDetails: (event) => event.metadata.message,
  },
  RecommendationCreated: {
    icon: 'ri-sticky-note-line',
    getDetails: (event) => (
      <RecommendationHistory
        type={event.metadata.type}
        message={event.metadata.message}
      />
    ),
  },
  SessionCreated: 'ri-edit-line',
  SessionUpdated: {
    icon: 'ri-edit-line',
    rightChildren: (event) => (
      <SessionChangedHistoryText
        changedProperties={event.metadata.changedProperties}
        status={event.metadata.status}
      />
    ),
  },
  SessionClosed: 'ri-stop-line',
  RecruitmentCompleted: 'ri-pause-line',
  RecruitmentPaused: 'ri-pause-line',
  RecruitmentLaunched: 'ri-play-line',
  RecruitmentResumed: 'ri-play-line',
  BulkContactSent: {
    icon: 'ri-mail-send-line',
    getDetails: (event) => (
      <Trans
        i18nKey="eventHistory.bulkContactSentDetails"
        values={event.metadata}
      />
    ),
  },
  InvoiceCreated: {
    icon: 'ri-file-add-line',
    getDetails: (event) => event.metadata.invoiceName,
  },
  InvoiceOpened: {
    icon: 'ri-file-list-3-line',
    getDetails: (event) => event.metadata.invoiceName,
  },
  InvoicePaid: {
    icon: 'ri-money-euro-circle-line',
    getDetails: (event) => event.metadata.invoiceName,
  },
};

export const AdminMonitoringContent = ({ session }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const sessionId = session.id;
  const { data: events, isLoading } = useGetSessionHistory(sessionId);
  const { t } = useTranslation();

  return (
    <div className={classes.wrapper}>
      <SessionBoxInfo
        color="purple"
        title="History"
        icon="ri-calendar-event-line"
        className={classNames(classes.card, classes.cardHistory)}
      >
        {events && <EventHistory events={events} eventsConfig={eventConfig} />}
      </SessionBoxInfo>

      <TargetCard
        className={classNames(classes.card, classes.cardHigh)}
        recruitmentId={session.recruitmentId}
      />

      {session?.planId && (
        <SessionBoxInfo
          color="inkSecondary"
          title={t('sessions.details.monitoringAdmin.option')}
          className={classes.card}
          icon="ri-settings-3-line"
        >
          <OptionsList planId={session.planId} />
        </SessionBoxInfo>
      )}

      <AdminNoteCard className={classes.card} sessionId={session.id} />
      <ReferralCard className={classes.card} sessionId={session.id} />
      <RecommendationCard className={classes.card} sessionId={session.id} />

      <BillingCard className={classes.billingCard} session={session} />
      {isLoading && <CenteredLoader />}
    </div>
  );
};
